import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";

/**
 * Phase 4F — Slice 1: Read-only Knowledge Base foundation.
 *
 * Scope of this module:
 *   - Bootstrap the caller's workspace via GET /api/me/workspace.
 *   - Fetch the workspace-scoped document list via
 *     GET /api/workspaces/{workspace_id}/documents.
 *   - Provide client-side search (OR across title AND original_filename)
 *     and client-side filter by DocumentStatus.
 *
 * The original Slice 1 foundation was extended by later Phase 4F
 * slices with detail, upload, retry, delete, and bounded status polling.
 *
 * Contract guarantee: this module reads ONLY fields returned by the list
 * endpoint (document_id, title, original_filename, mime_type, size_bytes,
 * content_sha256, status, created_at, updated_at). It never displays
 * content_sha256 to the user. It never infers worker health.
 */

// DocumentStatus values from backend/models/ingestion.py.
export const DOCUMENT_STATUSES = [
	"uploaded",
	"extracting",
	"chunking",
	"embedding",
	"ready",
	"failed",
];

// Phase 4F — Slice 6: bounded status polling.
export const POLLABLE_DOCUMENT_STATUSES = new Set([
	"uploaded",
	"extracting",
	"chunking",
	"embedding",
]);

export const KNOWLEDGE_BASE_POLL_INTERVAL_MS = 5000;
export const KNOWLEDGE_BASE_POLL_MAX_ATTEMPTS = 60;

export function hasPollableDocuments(documents) {
	return (
		Array.isArray(documents) &&
		documents.some(
			(doc) =>
				doc &&
				typeof doc === "object" &&
				POLLABLE_DOCUMENT_STATUSES.has(doc.status)
		)
	);
}

export const STATUS_LABELS = {
	uploaded: "Uploaded",
	extracting: "Extracting",
	chunking: "Chunking",
	embedding: "Embedding",
	ready: "Ready",
	failed: "Failed",
};

// Tailwind classes for the status badge. Neutral, deriving only from the
// backend-reported status. Never used to imply worker health.
export const STATUS_TONES = {
	uploaded: "bg-slate-100 text-slate-700 border-slate-200",
	extracting: "bg-amber-50 text-amber-800 border-amber-200",
	chunking: "bg-amber-50 text-amber-800 border-amber-200",
	embedding: "bg-amber-50 text-amber-800 border-amber-200",
	ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
	failed: "bg-red-50 text-red-800 border-red-200",
};

export function statusToLabel(status) {
	if (typeof status !== "string") return "Unknown";
	return STATUS_LABELS[status] || "Unknown";
}

export function statusToTone(status) {
	if (typeof status !== "string") return "bg-neutral-100 text-neutral-700 border-neutral-200";
	return STATUS_TONES[status] || "bg-neutral-100 text-neutral-700 border-neutral-200";
}

// Human byte formatter for the size column. Uses binary units to match the
// backend's per-format size caps (mebibytes).
export function formatBytes(bytes) {
	if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return "—";
	if (bytes < 1024) return `${bytes} B`;
	const kib = bytes / 1024;
	if (kib < 1024) return `${kib.toFixed(kib < 10 ? 1 : 0)} KiB`;
	const mib = kib / 1024;
	return `${mib.toFixed(mib < 10 ? 1 : 0)} MiB`;
}

function normalize(value) {
	if (typeof value !== "string") return "";
	return value.toLowerCase().trim();
}

/**
 * True if the query is empty (match all) or if the normalized query is a
 * substring of the normalized title OR the normalized original_filename.
 * This is a real OR across two fields, not a substring match against the
 * concatenation of one populated field.
 */
export function matchesSearch(doc, query) {
	const q = normalize(query);
	if (q.length === 0) return true;
	if (!doc || typeof doc !== "object") return false;
	const t = normalize(doc.title);
	const f = normalize(doc.original_filename);
	return t.includes(q) || f.includes(q);
}

export function matchesStatus(doc, statusFilter) {
	if (!statusFilter || statusFilter === "all") return true;
	if (!doc || typeof doc !== "object") return false;
	return doc.status === statusFilter;
}

function isCancel(err) {
	return (
		(axios.isCancel && axios.isCancel(err)) ||
		err?.code === "ERR_CANCELED" ||
		err?.name === "CanceledError"
	);
}

function classifyListError(err) {
	if (!err || !err.response) return "network_error";
	const status = err.response.status;
	if (status === 401) return "silent"; // interceptor signs out
	if (status === 403) return "permission_denied";
	return "unexpected_error";
}

/**
 * Read-only Knowledge Base state hook.
 *
 * Returned state:
 *   workspaceState : "loading" | "ready" | "unavailable"
 *   workspaceId    : string | null
 *   listState      : "idle" | "loading" | "ready" | "network_error"
 *                    | "permission_denied" | "unexpected_error"
 *   documents      : DocumentListItem[]  (raw from list endpoint)
 *   filtered       : DocumentListItem[]  (search + status filter applied)
 *   searchQuery    : string
 *   statusFilter   : "all" | DocumentStatus
 *   refetch        : () => Promise<void>
 */
export function useKnowledgeBase() {
	const mountedRef = useRef(true);
	const wsAbortRef = useRef(null);
	const listAbortRef = useRef(null);

	const [workspaceState, setWorkspaceState] = useState("loading");
	const [workspaceId, setWorkspaceId] = useState(null);
	const [listState, setListState] = useState("idle");
	const [documents, setDocuments] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	const runListFetch = useCallback(
		async (wsId, { background = false } = {}) => {
			if (!wsId) return false;

			// Polling must never overlap or interrupt another list request.
			if (background && listAbortRef.current) return false;

			listAbortRef.current?.abort();
			const controller = new AbortController();
			listAbortRef.current = controller;

			if (mountedRef.current && !background) {
				setListState("loading");
			}

			try {
				const { data } = await api.get(
					`/workspaces/${wsId}/documents`,
					{ signal: controller.signal }
				);
				if (!mountedRef.current) return false;
				if (controller.signal.aborted) return false;

				const arr = Array.isArray(data) ? data : [];
				setDocuments(arr);
				setListState("ready");
				return true;
			} catch (err) {
				if (isCancel(err)) return false;
				if (!mountedRef.current) return false;
				if (listAbortRef.current !== controller) return false;

				// Preserve the last successful list on polling failures.
				if (background) return true;

				const kind = classifyListError(err);
				if (kind === "silent") return true;
				setDocuments([]);
				setListState(kind);
				return true;
			} finally {
				if (listAbortRef.current === controller) {
					listAbortRef.current = null;
				}
			}
		},
		[]
	);

	// Workspace bootstrap on mount. Never accepts workspace_id from anywhere
	// but the authenticated /me/workspace call.
	useEffect(() => {
		mountedRef.current = true;
		const controller = new AbortController();
		wsAbortRef.current = controller;
		(async () => {
			try {
				const { data } = await api.get("/me/workspace", { signal: controller.signal });
				if (!mountedRef.current) return;
				if (controller.signal.aborted) return;
				if (data && typeof data.workspace_id === "string" && data.workspace_id.length > 0) {
					setWorkspaceId(data.workspace_id);
					setWorkspaceState("ready");
					runListFetch(data.workspace_id);
				} else {
					setWorkspaceState("unavailable");
				}
			} catch (err) {
				if (isCancel(err)) return;
				if (!mountedRef.current) return;
				setWorkspaceState("unavailable");
			}
		})();
		return () => {
			mountedRef.current = false;
			controller.abort();
			listAbortRef.current?.abort();
			listAbortRef.current = null;
			wsAbortRef.current = null;
		};
	}, [runListFetch]);

	const refetch = useCallback(async () => {
		if (workspaceId) {
			await runListFetch(workspaceId);
		}
	}, [workspaceId, runListFetch]);

	const shouldPoll =
		workspaceState === "ready" &&
		listState === "ready" &&
		hasPollableDocuments(documents);

	useEffect(() => {
		if (!shouldPoll || !workspaceId) return undefined;

		let cancelled = false;
		let timerId = null;
		let attempts = 0;

		const scheduleNext = () => {
			if (cancelled) return;
			if (attempts >= KNOWLEDGE_BASE_POLL_MAX_ATTEMPTS) return;
			timerId = setTimeout(
				runPoll,
				KNOWLEDGE_BASE_POLL_INTERVAL_MS
			);
		};

		const runPoll = async () => {
			if (cancelled) return;

			// Hidden tabs make no polling network requests.
			if (
				typeof document !== "undefined" &&
				document.visibilityState === "hidden"
			) {
				scheduleNext();
				return;
			}

			const attempted = await runListFetch(
				workspaceId,
				{ background: true }
			);

			if (cancelled) return;
			if (attempted) attempts += 1;
			scheduleNext();
		};

		scheduleNext();

		return () => {
			cancelled = true;
			if (timerId !== null) clearTimeout(timerId);
		};
	}, [shouldPoll, workspaceId, runListFetch]);

	const filtered = documents.filter(
		(d) => matchesSearch(d, searchQuery) && matchesStatus(d, statusFilter)
	);

	return {
		workspaceState,
		workspaceId,
		listState,
		documents,
		filtered,
		searchQuery,
		setSearchQuery,
		statusFilter,
		setStatusFilter,
		refetch,
	};
}

// ---------------------------------------------------------------------------
// Phase 4F — Slice 3: Document upload.
// ---------------------------------------------------------------------------

// Per-extension size caps, mirrored from backend/ingestion/mime_sniff.py.
// Client validation is UX-only; the backend remains authoritative.
export const UPLOAD_LIMITS_BYTES = {
	txt: 10 * 1024 * 1024,
	md: 10 * 1024 * 1024,
	docx: 25 * 1024 * 1024,
	pdf: 25 * 1024 * 1024,
};

export const UPLOAD_ALLOWED_EXTENSIONS = ["pdf", "docx", "txt", "md"];

export function extensionOf(fileName) {
	if (typeof fileName !== "string") return "";
	const idx = fileName.lastIndexOf(".");
	if (idx < 0 || idx === fileName.length - 1) return "";
	return fileName.slice(idx + 1).toLowerCase();
}

export function titleFromFilename(fileName) {
	if (typeof fileName !== "string" || fileName.length === 0) return "";
	const idx = fileName.lastIndexOf(".");
	return (idx > 0 ? fileName.slice(0, idx) : fileName).trim();
}

/**
 * Client-side preflight. UX-only.
 * Returns null when valid, or a stable error key string when invalid.
 * The backend remains the security boundary.
 */
export function preflightUpload({ file, title }) {
	if (!file || typeof file !== "object") return "no_file";
	if (typeof file.name !== "string" || file.name.length === 0) return "no_file";
	const trimmedTitle = typeof title === "string" ? title.trim() : "";
	if (trimmedTitle.length === 0) return "title_required";
	if (trimmedTitle.length > 200) return "title_too_long";
	// Reject obvious path traversal / control chars in filename for UX only.
	if (/[\\/\0]/.test(file.name)) return "unsupported_extension";
	const ext = extensionOf(file.name);
	if (!UPLOAD_ALLOWED_EXTENSIONS.includes(ext)) return "unsupported_extension";
	const cap = UPLOAD_LIMITS_BYTES[ext];
	if (typeof file.size === "number" && file.size > cap) return "file_too_large";
	// Zero-byte files are NOT rejected client-side; backend/extraction decides.
	// File.type is NOT used as an authoritative gate (browser differences).
	return null;
}

// Public codes the upload route can emit (source: backend/ingestion/errors.py).
// Kept local to give each code stable, non-sensitive user copy. Unknown codes
// fall through to a generic message.
export const UPLOAD_ERROR_COPY = {
	unsupported_file_type: "This file type is not supported. Allowed: PDF, DOCX, TXT, MD.",
	mime_mismatch: "The file's contents do not match its extension.",
	file_too_large: "The uploaded file exceeds the size limit for its type.",
	encrypted_document: "Encrypted documents cannot be ingested.",
	password_protected: "Password-protected documents cannot be ingested.",
	page_limit_exceeded: "This document exceeds the supported page limit.",
	character_limit_exceeded: "This document exceeds the supported character limit.",
	workspace_mismatch: "You do not have access to this workspace.",
	database_temporary_failure: "Please try again shortly.",
	// Client-side preflight keys.
	no_file: "Please select a file to upload.",
	title_required: "Please enter a title.",
	title_too_long: "Titles are limited to 200 characters.",
	unsupported_extension: "This file type is not supported. Allowed: PDF, DOCX, TXT, MD.",
};

/**
 * Parse an axios upload error into a stable local error key.
 * Reads only response.data.detail.error / .message per backend contract.
 * Never returns raw axios messages, raw payload strings, or stack traces.
 */
export function parseUploadError(err) {
	if (!err) return { kind: "unexpected", code: null };
	if (isCancel(err)) return { kind: "canceled", code: null };
	if (!err.response) return { kind: "network", code: null };
	const status = err.response.status;
	if (status === 401) return { kind: "silent", code: null };
	const detail = err.response.data && err.response.data.detail;
	// FastAPI 422 renders detail as an array — treat as generic.
	if (status === 422 || Array.isArray(detail)) {
		return { kind: "validation", code: null };
	}
	const code =
		detail && typeof detail === "object" && typeof detail.error === "string"
			? detail.error
			: null;
	if (code && Object.prototype.hasOwnProperty.call(UPLOAD_ERROR_COPY, code)) {
		return { kind: "coded", code };
	}
	return { kind: "unexpected", code: null };
}

export function uploadErrorCopyFor(result) {
	if (!result) return UPLOAD_ERROR_COPY.unsupported_extension;
	if (result.kind === "coded" && result.code) return UPLOAD_ERROR_COPY[result.code];
	if (result.kind === "network") return "We couldn't reach the server. Please check your connection and try again.";
	if (result.kind === "validation") return "The upload was rejected. Please check the file and try again.";
	return "Something went wrong — please try again.";
}

export function preflightErrorCopy(key) {
	return UPLOAD_ERROR_COPY[key] || "The upload could not be prepared. Please try again.";
}

// JobStatusLabel values from backend/models/ingestion.py.
export const JOB_STATUS_LABELS = {
	queued: "Queued",
	in_progress: "In progress",
	completed: "Completed",
	retryable_failed: "Failed (retryable)",
	permanent_failed: "Failed (permanent)",
	cancelled: "Cancelled",
};

export function jobStatusToLabel(status) {
	if (typeof status !== "string" || status.length === 0) return "Unknown";
	return JOB_STATUS_LABELS[status] || "Unknown";
}

/**
 * Parse a detail-endpoint axios error into a stable frontend category.
 *
 * Contract respected (source of truth: backend/routes/documents.py::_raise):
 *   response.data.detail.error   : short public code
 *   response.data.detail.message : short public message
 *
 * Anything not matching this shape falls back to a neutral "unexpected"
 * category. Raw response bodies and axios error messages are never leaked.
 */
export function parseDetailError(err) {
	if (!err) return { kind: "unexpected" };
	if (isCancel(err)) return { kind: "canceled" };
	if (!err.response) return { kind: "network" };
	const status = err.response.status;
	// 401 is handled globally by the axios response interceptor.
	if (status === 401) return { kind: "silent" };
	const detail = err.response.data && err.response.data.detail;
	const code =
		detail && typeof detail === "object" && typeof detail.error === "string"
			? detail.error
			: null;
	const message =
		detail && typeof detail === "object" && typeof detail.message === "string"
			? detail.message
			: null;
	if (status === 403) return { kind: "permission", code, message };
	if (status === 404) return { kind: "not_found", code, message };
	return { kind: "unexpected", code, message };
}

/**
 * On-demand detail hook. Fetches
 *   GET /workspaces/{workspaceId}/documents/{documentId}
 * only when documentId transitions to a non-null value. Aborts any pending
 * request when documentId changes, when the caller clears documentId
 * (Sheet close), or when the component unmounts. A generation counter
 * guards against a stale response from a previously selected document
 * overwriting the current one.
 *
 * Returned state:
 *   detailState  : "idle" | "loading" | "ready" | "permission_denied"
 *                  | "not_found" | "network_error" | "unexpected_error"
 *   detail       : DocumentDetail | null
 *   errorInfo    : { code: string|null, message: string|null } | null
 */
export function useDocumentDetail(workspaceId, documentId) {
	const mountedRef = useRef(true);
	const abortRef = useRef(null);
	const genRef = useRef(0);
	const [detailState, setDetailState] = useState("idle");
	const [detail, setDetail] = useState(null);
	const [errorInfo, setErrorInfo] = useState(null);
	// Slice 5B: an explicit refresh trigger. Incrementing this tick re-runs the
	// fetch effect for the SAME (workspaceId, documentId) pair. Callers use it
	// via the returned refetchDetail() so a mutating action (e.g. retry) can
	// force a deterministic follow-up GET rather than relying on parent
	// re-renders.
	const [refreshTick, setRefreshTick] = useState(0);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			abortRef.current?.abort();
			abortRef.current = null;
		};
	}, []);

	useEffect(() => {
		// Abort any prior in-flight request whenever the target changes.
		abortRef.current?.abort();
		abortRef.current = null;

		if (!workspaceId || !documentId) {
			setDetailState("idle");
			setDetail(null);
			setErrorInfo(null);
			return undefined;
		}

		genRef.current += 1;
		const myGen = genRef.current;
		const controller = new AbortController();
		abortRef.current = controller;
		setDetailState("loading");
		setDetail(null);
		setErrorInfo(null);

		api
			.get(`/workspaces/${workspaceId}/documents/${documentId}`, {
				signal: controller.signal,
			})
			.then((res) => {
				if (!mountedRef.current) return;
				if (controller.signal.aborted) return;
				// Stale response: caller has already selected a different document.
				if (genRef.current !== myGen) return;
				setDetail(res && res.data ? res.data : null);
				setErrorInfo(null);
				setDetailState("ready");
			})
			.catch((err) => {
				if (!mountedRef.current) return;
				if (genRef.current !== myGen) return;
				const parsed = parseDetailError(err);
				if (parsed.kind === "canceled" || parsed.kind === "silent") return;
				setDetail(null);
				setErrorInfo({ code: parsed.code ?? null, message: parsed.message ?? null });
				setDetailState(
					parsed.kind === "permission"
						? "permission_denied"
						: parsed.kind === "not_found"
							? "not_found"
							: parsed.kind === "network"
								? "network_error"
								: "unexpected_error"
				);
			});

		return () => {
			controller.abort();
			if (abortRef.current === controller) abortRef.current = null;
		};
	}, [workspaceId, documentId, refreshTick]);

	const refetchDetail = useCallback(() => {
		if (!mountedRef.current) return;
		if (!workspaceId || !documentId) return;
		setRefreshTick((t) => t + 1);
	}, [workspaceId, documentId]);

	return { detailState, detail, errorInfo, refetchDetail };
}

// ---------------------------------------------------------------------------
// Phase 4F — Slice 4: Document delete.
// ---------------------------------------------------------------------------

// Stable user-facing copy for delete outcomes. Keys map to the parsed
// error kind — never to raw axios messages, raw backend payloads, or
// stack traces.
export const DELETE_ERROR_COPY = {
	permission: "You do not have permission to delete this document.",
	not_found: "This document is no longer available in your workspace.",
	validation: "The request was rejected. Please refresh and try again.",
	service_unavailable: "This service is temporarily unavailable. Please try again shortly.",
	network: "We couldn't reach the server. Please check your connection and try again.",
	unexpected: "Something went wrong — please try again.",
};

/**
 * Parse an axios delete error into a stable local kind.
 *
 * Contract respected (source of truth: backend/routes/documents.py::_raise
 * and backend/ingestion/errors.py):
 *   response.data.detail.error   : short public code
 *   response.data.detail.message : short public message
 *
 * Never returns raw axios messages, raw payload strings, or stack traces.
 */
export function parseDeleteError(err) {
	if (!err) return { kind: "unexpected" };
	if (isCancel(err)) return { kind: "canceled" };
	if (!err.response) return { kind: "network" };
	const status = err.response.status;
	// 401 is handled globally by the axios response interceptor.
	if (status === 401) return { kind: "silent" };
	const detail = err.response.data && err.response.data.detail;
	// FastAPI 422 renders detail as an array — treat as generic validation.
	if (status === 422 || Array.isArray(detail)) return { kind: "validation" };
	if (status === 403) return { kind: "permission" };
	if (status === 404) return { kind: "not_found" };
	if (status === 503) return { kind: "service_unavailable" };
	return { kind: "unexpected" };
}

export function deleteErrorCopyFor(result) {
	if (!result) return DELETE_ERROR_COPY.unexpected;
	return DELETE_ERROR_COPY[result.kind] || DELETE_ERROR_COPY.unexpected;
}

/**
 * Issue a soft-delete against a workspace-scoped document.
 *
 * Contract:
 *   DELETE /workspaces/{workspaceId}/documents/{documentId}  ->  204
 *
 * Rejects with the raw axios error so the caller can invoke parseDeleteError
 * against a single, well-known shape. The caller is responsible for guarding
 * state updates via mountedRef and preventing duplicate submissions via
 * inFlightRef.
 */
export function deleteDocument(workspaceId, documentId) {
	return api.delete(`/workspaces/${workspaceId}/documents/${documentId}`);
}

// ---------------------------------------------------------------------------
// Phase 4F — Slice 5B: Document retry (manual ingestion retry).
// ---------------------------------------------------------------------------

// Stable user-facing copy for retry outcomes. Keys map to the parsed error
// kind — never to raw axios messages, raw backend payloads, or stack
// traces. The two backend-issued 409 hints get dedicated messages; every
// other transport-level outcome maps to a generic authored string.
export const RETRY_ERROR_COPY = {
	retry_not_eligible:       "This document is not currently eligible for retry.",
	active_retry_in_progress: "A retry is already in progress for this document.",
	permission:               "You do not have permission to retry this document.",
	validation:               "The request was rejected. Please refresh and try again.",
	service_unavailable:      "This service is temporarily unavailable. Please try again shortly.",
	network:                  "We couldn't reach the server. Please check your connection and try again.",
	unexpected:               "Something went wrong — please try again.",
};

// Public codes the retry route can return in detail.error. Source of truth:
// backend/routes/documents.py + backend/ingestion/errors.py.
const RETRY_CODED_ERRORS = new Set(["retry_not_eligible", "active_retry_in_progress"]);

/**
 * Parse an axios retry error into a stable local descriptor.
 *
 * Contract respected (source of truth: backend/routes/documents.py::_raise
 * and backend/ingestion/errors.py):
 *   response.data.detail.error   : short public code
 *   response.data.detail.message : short public message
 *
 * Returned shape:
 *   { kind: "canceled" }
 *   { kind: "silent" }                                          // 401
 *   { kind: "coded", code: "retry_not_eligible" | "active_retry_in_progress" }   // 409
 *   { kind: "permission" }                                      // 403
 *   { kind: "validation" }                                      // 422
 *   { kind: "service_unavailable" }                             // 503
 *   { kind: "network" }                                         // no response
 *   { kind: "unexpected" }                                      // everything else
 *
 * Never returns raw axios messages, raw payload strings, or stack traces.
 */
export function parseRetryError(err) {
	if (!err) return { kind: "unexpected" };
	if (isCancel(err)) return { kind: "canceled" };
	if (!err.response) return { kind: "network" };
	const status = err.response.status;
	// 401 is handled globally by the axios response interceptor (sign-out).
	// Locally we simply treat it as silent so the retry flow shows no banner
	// and no success toast.
	if (status === 401) return { kind: "silent" };
	const detail = err.response.data && err.response.data.detail;
	// FastAPI 422 renders detail as an array — treat as generic validation.
	if (status === 422 || Array.isArray(detail)) return { kind: "validation" };
	if (status === 403) return { kind: "permission" };
	if (status === 503) return { kind: "service_unavailable" };
	if (status === 409) {
		const code =
			detail && typeof detail === "object" && typeof detail.error === "string"
				? detail.error
				: null;
		if (code && RETRY_CODED_ERRORS.has(code)) return { kind: "coded", code };
		// A 409 without a recognized hint is treated as a generic conflict.
		return { kind: "unexpected" };
	}
	return { kind: "unexpected" };
}

export function retryErrorCopyFor(result) {
	if (!result) return RETRY_ERROR_COPY.unexpected;
	if (result.kind === "coded" && result.code) return RETRY_ERROR_COPY[result.code];
	return RETRY_ERROR_COPY[result.kind] || RETRY_ERROR_COPY.unexpected;
}

/**
 * Issue a manual retry against a workspace-scoped failed document.
 *
 * Contract:
 *   POST /workspaces/{workspaceId}/documents/{documentId}/retry  ->  200
 *
 * Success body: { document_id, status: "uploaded", job_id }.
 * Expected non-catastrophic 409 hints: retry_not_eligible, active_retry_in_progress.
 *
 * Rejects with the raw axios error so the caller can invoke parseRetryError
 * against a single, well-known shape.
 */
export function retryDocument(workspaceId, documentId) {
	return api.post(`/workspaces/${workspaceId}/documents/${documentId}/retry`);
}
